import type { EditSpecDict, EditSpecSourceRefDict } from '../../../../generated/nodeDataTypes'
import type { SelectedReference } from '../../nodes/shared/ReferenceSelector'
import { fetchMediaBlob } from '../../../../api/mediaFetch'

async function durationOf(projectId: string, reference: SelectedReference): Promise<number> {
  const response = await fetchMediaBlob(projectId, reference.assetPath, { signal: AbortSignal.timeout(30000) })
  if (!response.ok) throw new Error(`Could not load ${reference.label}: HTTP ${response.status}`)
  const url = URL.createObjectURL(await response.blob())
  const element = document.createElement(reference.mediaType === 'audio' ? 'audio' : 'video')
  try {
    return await new Promise<number>((resolve, reject) => {
      const timer = setTimeout(() => reject(new Error(`Media duration timed out: ${reference.label}`)), 15000)
      const finish = () => clearTimeout(timer)
      element.onloadedmetadata = () => { finish(); Number.isFinite(element.duration) && element.duration > 0 ? resolve(element.duration) : reject(new Error(`Invalid media duration: ${reference.label}`)) }
      element.onerror = () => { finish(); reject(new Error(element.error?.message || `Cannot read ${reference.label}`)) }
      element.preload = 'metadata'; element.src = url
    })
  } finally { element.removeAttribute('src'); element.load(); URL.revokeObjectURL(url) }
}

/** Import real grounded sources into the current canonical document, retaining all Text edits. */
export async function appendEditSpecMedia(spec: EditSpecDict, references: SelectedReference[], projectId: string): Promise<EditSpecDict> {
  let next = spec
  for (const reference of references) {
    if (!reference.nodeId || reference.fromIndex !== undefined) throw new Error('This source cannot be represented by an EditSpec media reference')
    const source: EditSpecSourceRefDict = reference.mediaType === 'video'
      ? { node_id: reference.nodeId, output_port: 'out_video', media_type: 'video' }
      : reference.mediaType === 'audio' ? { node_id: reference.nodeId, output_port: 'out_audio', media_type: 'audio' }
      : (() => { throw new Error('Image media is not supported by this Editor') })()
    if (reference.sourceHandle && reference.sourceHandle !== source.output_port) throw new Error(`Unsupported EditSpec output port: ${reference.sourceHandle}`)
    const known = [...next.clips.map((clip) => clip.source), ...(next.audio?.tracks ?? []).map((track) => track.source)]
    if (known.some((item) => item.node_id === source.node_id && item.output_port === source.output_port)) continue
    const duration = await durationOf(projectId, reference)
    if (reference.mediaType === 'video') next = { ...next, clips: [...next.clips, { id: crypto.randomUUID(), source, in_seconds: 0, out_seconds: duration }] }
    else next = { ...next, audio: { ...next.audio, tracks: [...(next.audio?.tracks ?? []), {
      id: crypto.randomUUID(), source, role: 'sfx', start_seconds: 0, end_seconds: duration, gain: 1,
    }] } }
  }
  return next
}
